import { MouseEvent } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { DailyExpenseReviewRow, DailyExpenseTotals } from "../../types/daily-expense.type";
import { formatNumber, numberWithCommas } from "../../utils";
import DailyExpenseStatusChip from "./DailyExpenseStatusChip";
import { EMPTY_CELL_TEXT, UNIT_TEXT } from "./constants";
import { chipKindOf } from "./dailyExpenseStatus";
import { isSelectableRow, selectableUuidsOf } from "./dailyExpenseReview";
import { formatBusinessDate, formatTimestamp, weekdayShortOf } from "./format";
import {
  CLICKABLE_ROW_SX,
  FOOT_ROW_SX,
  HEAD_ROW_SX,
  TABLE_SX,
  tableContainerSx,
} from "./tableStyles";

const TABLE_MAX_HEIGHT = 580;
/** Columns after the checkbox: วันที่, สาขา, ฝ่ายขาย, ฝ่ายบริหาร, รวม, ผู้ส่ง, เวลาส่ง, สถานะ. */
const AMOUNT_COLUMN_OFFSET = 2;

interface ReviewTableProps {
  title: string;
  rows: DailyExpenseReviewRow[];
  totals: DailyExpenseTotals;
  /** Ticked uuids, already narrowed to rows that can still be approved. */
  selected: string[];
  isApproving: boolean;
  onToggle: (uuid: string) => void;
  onToggleAll: () => void;
  onOpen: (row: DailyExpenseReviewRow) => void;
  onApproveSelected: () => void;
}

/**
 * S3 list: one row per branch × day. A click opens the document; the box in front of a document
 * waiting for head office ticks it for อนุมัติที่เลือก. Documents in any other state carry no box.
 */
export default function ReviewTable({
  title,
  rows,
  totals,
  selected,
  isApproving,
  onToggle,
  onToggleAll,
  onOpen,
  onApproveSelected,
}: Readonly<ReviewTableProps>) {
  const selectableCount = selectableUuidsOf(rows).length;
  const hasSelectColumn = selectableCount > 0;
  const openRow = (row: DailyExpenseReviewRow) => () => onOpen(row);
  // The box sits inside a clickable row: ticking it must not also open the drawer.
  const stopRowClick = (event: MouseEvent) => event.stopPropagation();
  const toggleRow = (row: DailyExpenseReviewRow) => () => onToggle(row.uuid);

  return (
    <Paper>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap" mb={1.5}>
        <Stack direction="row" alignItems="baseline" gap={1.25}>
          <Typography fontWeight={600}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {UNIT_TEXT}
          </Typography>
        </Stack>
        {selected.length > 0 && (
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography variant="body2" color="text.secondary">
              เลือก {numberWithCommas(selected.length)} ใบ
            </Typography>
            <Button
              variant="contained"
              startIcon={<DoneAllIcon />}
              onClick={onApproveSelected}
              loading={isApproving}
            >
              อนุมัติที่เลือก ({numberWithCommas(selected.length)})
            </Button>
          </Stack>
        )}
      </Stack>
      <TableContainer sx={tableContainerSx(TABLE_MAX_HEIGHT)}>
        <Table stickyHeader size="small" sx={TABLE_SX}>
          <TableHead>
            <TableRow sx={HEAD_ROW_SX}>
              {hasSelectColumn && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.length === selectableCount}
                    indeterminate={selected.length > 0 && selected.length < selectableCount}
                    onChange={onToggleAll}
                    slotProps={{ input: { "aria-label": "เลือกทุกใบที่รออนุมัติ" } }}
                  />
                </TableCell>
              )}
              <TableCell>วันที่</TableCell>
              <TableCell>สาขา</TableCell>
              <TableCell align="right">ฝ่ายขาย</TableCell>
              <TableCell align="right">ฝ่ายบริหาร</TableCell>
              <TableCell align="right">รวม</TableCell>
              <TableCell>ผู้ส่ง</TableCell>
              <TableCell>เวลาส่ง</TableCell>
              <TableCell>สถานะ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.uuid} onClick={openRow(row)} sx={CLICKABLE_ROW_SX}>
                {hasSelectColumn && (
                  <TableCell padding="checkbox" onClick={stopRowClick}>
                    {isSelectableRow(row) && (
                      <Checkbox
                        checked={selected.includes(row.uuid)}
                        onChange={toggleRow(row)}
                        slotProps={{
                          input: {
                            "aria-label": `เลือก ${row.branch_code} ${formatBusinessDate(row.business_date)}`,
                          },
                        }}
                      />
                    )}
                  </TableCell>
                )}
                <TableCell>
                  {formatBusinessDate(row.business_date)}{" "}
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {weekdayShortOf(row.business_date)}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600} component="span">
                    {row.branch_code}
                  </Typography>{" "}
                  <Typography variant="caption" color="text.secondary" component="span">
                    {row.branch_name}
                  </Typography>
                </TableCell>
                <TableCell align="right">{formatNumber(row.sales_total)}</TableCell>
                <TableCell align="right">{formatNumber(row.admin_total)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatNumber(row.total)}
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {row.submitted_by_name ?? EMPTY_CELL_TEXT}
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{formatTimestamp(row.submitted_at)}</TableCell>
                <TableCell>
                  <DailyExpenseStatusChip kind={chipKindOf(row.status, row.is_locked)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow sx={FOOT_ROW_SX}>
              <TableCell colSpan={hasSelectColumn ? AMOUNT_COLUMN_OFFSET + 1 : AMOUNT_COLUMN_OFFSET}>
                รวม {numberWithCommas(rows.length)} ใบ
              </TableCell>
              <TableCell align="right">{formatNumber(totals.sales_total)}</TableCell>
              <TableCell align="right">{formatNumber(totals.admin_total)}</TableCell>
              <TableCell align="right">{formatNumber(totals.total)}</TableCell>
              <TableCell colSpan={3} />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Paper>
  );
}
